/**
 * Invitation request/response schemas — single source of truth shared
 * by the backend NestJS DTOs and the frontend admin portal.
 *
 * BE controller: apps/api/src/modules/identity/interface/auth.controller.ts
 *   POST /auth/invitations/preview — body { token } → 200 {org_name, role, …}
 *   POST /auth/invitations/accept  — body { token, password? } → 200 {user_id, …}
 *
 * Both endpoints are anti-enum on token failures: ANY token-invalid
 * cause (unknown / wrong hash / expired / revoked / already-accepted /
 * concurrent-modification) returns a BYTE-IDENTICAL 401 with body
 * `{ error: 'invitation_token_invalid' }`. The FE relies on that
 * invariant to render a single "this invitation is not valid" surface.
 *
 * Token format note: tokens are issued as `inv_` + base64url(32 bytes)
 * by Argon2InvitationTokenIssuer. The schema only enforces non-empty;
 * stricter format checks happen on the server when verifying the
 * Argon2id hash (constant time). Putting a hex/length regex here
 * would not improve security and could false-reject tokens if the
 * BE encoding ever changes.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Shared field rules
// ---------------------------------------------------------------------------
/**
 * Display-name field rule (firstName / lastName on create-invitation and
 * the resulting User). Optional at the wire boundary — the BE accepts
 * invitations without a name (legacy/future API consumers that don't
 * surface the fields). The invite wizard imposes a stricter required
 * rule client-side.
 */
const NAME_MAX_LENGTH = 100;
export const invitationNameSchema = z
    .string()
    .min(1)
    .max(NAME_MAX_LENGTH)
    .optional();
// ---------------------------------------------------------------------------
// Preview — POST /auth/invitations/preview
// ---------------------------------------------------------------------------
export const previewInvitationBodySchema = z
    .object({
    token: z.string().min(1),
})
    .strict();
export const previewInvitationRoleSchema = z
    .object({
    slug: z.string(),
    display_name: z.string(),
})
    .strict();
export const previewInvitationResponseSchema = z
    .object({
    org_name: z.string(),
    role: previewInvitationRoleSchema,
    inviter_email_masked: z.string(),
    requires_new_account: z.boolean(),
})
    .strict();
// ---------------------------------------------------------------------------
// Accept — POST /auth/invitations/accept
// ---------------------------------------------------------------------------
// Password rule mirrors registerSchema / resetPasswordSchema (12..128).
// Optional because existing-account acceptances do not supply one.
export const acceptInvitationBodySchema = z
    .object({
    token: z.string().min(1),
    password: z.string().min(12).max(128).optional(),
})
    .strict();
export const acceptInvitationResponseSchema = z
    .object({
    user_id: z.string().uuid(),
    organization_id: z.string().uuid(),
    membership_id: z.string().uuid(),
    was_new_account: z.boolean(),
})
    .strict();
//# sourceMappingURL=invitation.js.map